import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, FormControl, Grid, ListItemText } from '@mui/material';
import { ColDef } from 'ag-grid-community';
import MenuItem from '@mui/material/MenuItem';
import EyeIcon from '@mui/icons-material/Visibility';
import EyeOffIcon from '@mui/icons-material/VisibilityOff';
import type { SelectionChangedEvent } from 'ag-grid-community/dist/types/core/events';
import { AgGridReact } from 'ag-grid-react';
import { useMutation } from '@tanstack/react-query';
import { ReleaseType, StrategicVolumeSummaryRow, DATE_FORMATS } from '@callmart/types';
import Container from '../components/Layout/Container';
import CustomDatePicker from '../components/UI/CustomDatePicker';
import { handleDatePickerMonthChange } from '../utils/componentUtils';
import {
    ColorizedNameRenderer,
    CorrectedTotalsStatusCellRenderer,
    OfferedReportStatusCellRenderer,
    StrategicVolumeStatusCellRenderer,
} from '../utils/agGridUtils';
import { formatNumber } from '../utils/numberUtils';
import GridContainer from '../components/Layout/GridContainer';
import CustomSelect from '../components/UI/CustomSelect';
import { useLocalStorage } from '../hooks/useLocalStorage';
import Tabs from '../components/UI/Tabs';
import OfferedReportsTab from '../components/BiddingOverview/OfferedReportsTab';
import StrategicVolumeReportsTab from '../components/BiddingOverview/StrategicVolumeReportsTab';
import CorrectedTotalsTab from '../components/BiddingOverview/CorrectedTotalsTab';
import ColumnOption from '../types/ColumnOption.interface';
import { ReleaseTypeText } from '../types/StrategicVolumeSummary';
import { ReleasedTypeText } from '../types/CorrectedTotalSummary';
import { OfferedReportStatusText } from '../types/OfferedReportSummary';
import { useExpireLocalStorage } from '../hooks/useExpireLocalStorage';
import { markStrategicVolumeReportsRelease } from '../services/strategicVolumeReport';
import { queryClient } from '../services/queryClient';
import { QueryKey } from '../enums/QueryKey';
import { getStartAndEndOfAMonth } from '../utils/dateUtils';
import { StrategicVolumeReleaseReportsForm } from '../types/StrategicVolume';
import { StrategicVolumeReportRelease } from '../enums/StrategicVolumeReportRelease';
import UserContext from '../store/User/UserContext';
import { useDispatchSuccessNotification } from '../hooks';
import { SumWithHistoryCellRenderer } from '../components/VolumeOverview/SumWithHistoryCellRenderer';
import SumHistoryPopover from '../components/VolumeOverview/SumHistoryPopover';
import CardPopover from '../components/UI/CardPopover';
import { useCellPopover } from '../hooks/useCellPopover';
import { useHEMTUsers } from '../hooks/api/useHEMTUsers';
import dayjs, { Dayjs } from '@utils/dayjs';

enum TABS {
    OFFERED_REPORTS,
    STRATEGIC_VOLUMES,
    CORRECTED_TOTALS,
}

const VolumeOverview: React.FC = () => {
    const { t } = useTranslation();
    const loggedInUser = useContext(UserContext);
    // TEF aliases to WFM in HEMTService. Memo name kept for blast radius in this
    // page; the flag now means "WFM-or-equivalent manager view".
    const isTEFUser = useMemo(() => Boolean(loggedInUser?.isWFMUser), [loggedInUser]);
    // Strictly tracks the backend write gate on /strategic-volume-report/markRelease.
    const canMarkRelease = Boolean(loggedInUser?.isWFMUser);
    const { dispatchSuccessNotification } = useDispatchSuccessNotification();

    const [selectedDate, setSelectedDate] = useExpireLocalStorage<string>(
        'volume-overview-selected-date',
        dayjs().toISOString()
    );
    const dayJsDate = useMemo(() => dayjs(selectedDate), [selectedDate]);
    const [reportsGroupBy, setReportsGroupBy] = useLocalStorage<string>(
        'volume-overview-reports-group-by',
        'hasOfferedReports'
    );
    const [volumesGroupBy, setVolumesGroupBy] = useLocalStorage<string>('volume-overview-volumes-group-by', 'release');
    const [totalsGroupBy, setTotalsGroupBy] = useLocalStorage<string>('volume-overview-totals-group-by', 'released');
    const [openedTab, setOpenedTab] = useLocalStorage<number>('volume-overview-tab', 0);
    const [reportsHiddenColumns, setReportsHiddenColumns] = useLocalStorage<string[]>(
        'volume-overview-reports-hidden-columns',
        []
    );
    const [volumesHiddenColumns, setVolumesHiddenColumns] = useLocalStorage<string[]>(
        'volume-overview-volumes-hidden-columns',
        []
    );
    const [totalsHiddenColumns, setTotalsHiddenColumns] = useLocalStorage<string[]>(
        'volume-overview-totals-hidden-columns',
        []
    );

    // History popover state
    const { data: users } = useHEMTUsers(true);
    const { hoveredCell, hoveredCellHistoryList, popoverOpen, popoverClose } = useCellPopover();

    const handleMarkedStrategicVolumeReports = useCallback(async () => {
        if (strategicVolumeRef?.current) {
            strategicVolumeRef.current.api.deselectAll();
        } else {
            setSelectedStrategicVolumeRows([]);
        }
        const { dateFrom, dateTo } = getStartAndEndOfAMonth(dayJsDate);

        await queryClient.invalidateQueries([
            QueryKey.StrategicVolumeReportsSummary,
            {
                dateFrom: dateFrom.format(DATE_FORMATS.ISO_DATETIME),
                dateTo: dateTo.format(DATE_FORMATS.ISO_DATETIME),
            },
        ]);

        dispatchSuccessNotification({
            message: t('volume.releaseStatusToggled'),
        });
    }, [dayJsDate, dispatchSuccessNotification, t]);

    const { mutate: handleMarkStrategicVolumeReportsRelease } = useMutation({
        mutationFn: markStrategicVolumeReportsRelease,
        onSuccess: handleMarkedStrategicVolumeReports,
    });

    const strategicVolumeRef = useRef<AgGridReact<StrategicVolumeSummaryRow>>(null);
    const [selectedStrategicVolumeRows, setSelectedStrategicVolumeRows] = useState<StrategicVolumeSummaryRow[]>([]);

    const handleStrategicVolumeSelection = useCallback((event: SelectionChangedEvent) => {
        setSelectedStrategicVolumeRows(event.api.getSelectedRows());
    }, []);

    const releaseSelectedStrategicVolumes = useCallback(() => {
        const selectedRows = strategicVolumeRef?.current
            ? strategicVolumeRef.current.api.getSelectedRows()
            : selectedStrategicVolumeRows;

        handleMarkStrategicVolumeReportsRelease({
            strategicVolumes: selectedRows.reduce((acc: StrategicVolumeReleaseReportsForm[], row) => {
                if (!row.volumeReports) return acc;
                for (const volumeId in row.volumeReports) {
                    acc.push({
                        strategicVolumeId: volumeId,
                        strategicVolumeReports: [
                            {
                                id: row.volumeReports[volumeId],
                                release: row.release,
                            },
                        ],
                    });
                }

                return acc;
            }, []),
            releaseType: StrategicVolumeReportRelease.READY_FOR_RELEASE,
        });
    }, [handleMarkStrategicVolumeReportsRelease, selectedStrategicVolumeRows]);

    const handleTabChange = useCallback(
        (tab: number) => {
            setSelectedStrategicVolumeRows([]);
            setOpenedTab(tab);
        },
        [setOpenedTab]
    );

    const hiddenColumns = useMemo(() => {
        switch (openedTab) {
            case TABS.OFFERED_REPORTS:
                return reportsHiddenColumns;
            case TABS.STRATEGIC_VOLUMES:
                return volumesHiddenColumns;
            case TABS.CORRECTED_TOTALS:
                return totalsHiddenColumns;
            default:
                return [];
        }
    }, [openedTab, totalsHiddenColumns, reportsHiddenColumns, volumesHiddenColumns]);

    const setHiddenColumns = useCallback(
        (columns: string[]) => {
            switch (openedTab) {
                case TABS.OFFERED_REPORTS:
                    setReportsHiddenColumns(columns);
                    break;
                case TABS.STRATEGIC_VOLUMES:
                    setVolumesHiddenColumns(columns);
                    break;
                case TABS.CORRECTED_TOTALS:
                    setTotalsHiddenColumns(columns);
                    break;
            }
        },
        [openedTab, setTotalsHiddenColumns, setReportsHiddenColumns, setVolumesHiddenColumns]
    );

    const groupBy = useMemo((): string => {
        switch (openedTab) {
            case TABS.OFFERED_REPORTS:
                return reportsGroupBy;
            case TABS.STRATEGIC_VOLUMES:
                return volumesGroupBy;
            case TABS.CORRECTED_TOTALS:
                return totalsGroupBy;
            default:
                return '';
        }
    }, [openedTab, reportsGroupBy, volumesGroupBy, totalsGroupBy]);

    const setGroupBy = useCallback(
        (value: string): void => {
            switch (openedTab) {
                case TABS.OFFERED_REPORTS:
                    setReportsGroupBy(value);
                    break;
                case TABS.STRATEGIC_VOLUMES:
                    setVolumesGroupBy(value);
                    break;
                case TABS.CORRECTED_TOTALS:
                    setTotalsGroupBy(value);
                    break;
            }
        },
        [openedTab, setReportsGroupBy, setVolumesGroupBy, setTotalsGroupBy]
    );

    const isColumnHidden = useCallback((column: string) => hiddenColumns.includes(column), [hiddenColumns]);
    const isColumnGrouped = useCallback((column: string) => groupBy === column, [groupBy]);
    const getColumnDef = useCallback(
        (column: string, { group, hide, alwaysHide }: { group: boolean; hide: boolean; alwaysHide?: boolean }) => {
            return {
                field: column,
                hide: alwaysHide || (hide ? isColumnHidden(column) : false),
                rowGroup: group ? isColumnGrouped(column) : false,
                context: {
                    canBeGrouped: group,
                    canBeHidden: hide,
                    alwaysHide: alwaysHide || false,
                },
            };
        },
        [isColumnGrouped, isColumnHidden]
    );

    // History click handler for offered reports
    const handleSumHistoryClick = useCallback(
        (rowData: any, anchorEl: HTMLElement) => {
            popoverOpen(true, anchorEl, rowData);
        },
        [popoverOpen]
    );

    const offeredReportsColumns = useMemo<(ColDef & ColumnOption)[]>(
        () => [
            {
                ...getColumnDef('offeredClusterId', { group: false, hide: false }),
                headerName: t('volume.offeredVolume'),
                valueGetter: ({ data }) => {
                    const worktypeText = data?.worktypeName || '';
                    const channelName = data?.channelName || '';
                    const taskName = data?.taskName || '';

                    return `${worktypeText} - ${channelName} - ${taskName}`;
                },
            },
            {
                ...getColumnDef('worktypeName', { group: true, hide: true, alwaysHide: true }),
                headerName: t('field.worktype'),
                enableRowGroup: true,
                hide: true,
            },
            {
                ...getColumnDef('channelName', { group: true, hide: true, alwaysHide: true }),
                headerName: t('field.channel'),
                enableRowGroup: true,
                hide: true,
            },
            {
                ...getColumnDef('taskName', { group: true, hide: true, alwaysHide: true }),
                headerName: t('field.task'),
                enableRowGroup: true,
                hide: true,
            },
            {
                ...getColumnDef('forecasters', { group: true, hide: true }),
                headerName: t('field.forecaster'),
                enableRowGroup: true,
                valueGetter: ({ data }) => (data?.forecasters || []).filter(Boolean),
                cellRenderer: ColorizedNameRenderer,
                cellRendererParams: {
                    emptyValue: t('common.unassigned'),
                },
            },
            {
                ...getColumnDef('sum', { group: false, hide: true }),
                headerName: t('common.sum'),
                cellRenderer: SumWithHistoryCellRenderer,
                cellRendererParams: {
                    onHistoryClick: handleSumHistoryClick,
                },
                // Same getter as the other two sum columns: the API sends `sum` as a string, so
                // without the coercion ag-grid's default comparator sorts it as text and puts
                // 10.722 before 1.218. Not falsy: a sum of 0 is data, '-' means nothing entered.
                valueGetter: ({ data }) => (data?.sum == null ? null : Math.round(data.sum)),
                valueFormatter: ({ value }) => (value == null ? '-' : formatNumber(value)),
            },
            {
                ...getColumnDef('hasOfferedReports', { group: true, hide: true }),
                headerName: t('field.status'),
                valueGetter: ({ data }) =>
                    data?.hasOfferedReports ? OfferedReportStatusText.SUCCESS : OfferedReportStatusText.ERROR,
                cellRenderer: OfferedReportStatusCellRenderer,
                initialSort: 'desc',
            },
        ],
        [getColumnDef, handleSumHistoryClick, t]
    );

    const strategicVolumesColumns = useMemo<(ColDef & ColumnOption)[]>(
        () => [
            {
                ...getColumnDef('id', { group: false, hide: false }),
                keyCreator: ({ data }) =>
                    [data.workingUnitId || data.id, (data.forecasterNames || []).join('-'), data.release].join('-'),
                headerName: t('volume.offeredVolume'),
                valueGetter: ({ data }) => {
                    const { worktypeName, channelName, taskName } = data || {};

                    return [worktypeName, channelName, taskName].filter(Boolean).join(' - ');
                },
                filterParams: {
                    valueFormatter: ({ value }: { value: any }) => value || '',
                },
            },
            {
                ...getColumnDef('worktypeName', { group: true, hide: true, alwaysHide: true }),
                headerName: t('field.worktype'),
                enableRowGroup: true,
                hide: true,
            },
            {
                ...getColumnDef('channelName', { group: true, hide: true, alwaysHide: true }),
                headerName: t('field.channel'),
                enableRowGroup: true,
                hide: true,
            },
            {
                ...getColumnDef('taskName', { group: true, hide: true, alwaysHide: true }),
                headerName: t('field.task'),
                enableRowGroup: true,
                hide: true,
            },
            {
                ...getColumnDef('forecasterNames', { group: true, hide: true }),
                headerName: t('field.forecaster'),
                enableRowGroup: true,
                valueGetter: ({ data }) => (data?.forecasterNames || []).filter(Boolean),
                cellRenderer: ColorizedNameRenderer,
                cellRendererParams: {
                    emptyValue: t('common.unassigned'),
                },
            },
            {
                ...getColumnDef('sum', { group: false, hide: true }),
                headerName: t('common.sum'),
                // Not falsy: a sum of 0 is data, '-' means nothing entered.
                valueGetter: ({ data }) => (data?.sum == null ? null : Math.round(data.sum)),
                valueFormatter: (params) => (params.value == null ? '-' : formatNumber(params.value)),
            },
            {
                ...getColumnDef('release', { group: true, hide: true }),
                headerName: t('field.status'),
                initialSort: 'desc',
                valueGetter: ({ data }) => {
                    switch (data?.release) {
                        case ReleaseType.NOT_RELEASED:
                            return ReleaseTypeText.NOT_RELEASED;
                        case ReleaseType.READY_FOR_RELEASE:
                            return ReleaseTypeText.READY_FOR_RELEASE;
                        case ReleaseType.RELEASED:
                            return ReleaseTypeText.RELEASED;
                        default:
                            return ReleaseTypeText.UNDEFINED;
                    }
                },
                cellRenderer: StrategicVolumeStatusCellRenderer,
            },
        ],
        [getColumnDef, t]
    );

    const totalsColumns = useMemo<(ColDef & ColumnOption)[]>(
        () => [
            {
                ...getColumnDef('id', { group: false, hide: false }),
                keyCreator: ({ data }) => [data.id, data.release].join('-'),
                headerName: t('volume.offeredVolume'),
                valueGetter: ({ data }) => {
                    const { worktypeName, channelName, taskName } = data || {};

                    return [worktypeName, channelName, taskName].filter(Boolean).join(' - ');
                },
                filterParams: {
                    valueFormatter: ({ value }: { value: any }) => value || '',
                },
            },
            {
                ...getColumnDef('worktypeName', { group: true, hide: true, alwaysHide: true }),
                headerName: t('field.worktype'),
                enableRowGroup: true,
                hide: true,
            },
            {
                ...getColumnDef('channelName', { group: true, hide: true, alwaysHide: true }),
                headerName: t('field.channel'),
                enableRowGroup: true,
                hide: true,
            },
            {
                ...getColumnDef('taskName', { group: true, hide: true, alwaysHide: true }),
                headerName: t('field.task'),
                enableRowGroup: true,
                hide: true,
            },
            {
                ...getColumnDef('forecasters', { group: true, hide: true }),
                valueGetter: ({ data }) => (data?.forecasters || []).filter(Boolean),
                headerName: t('field.forecaster'),
                enableRowGroup: true,
                cellRenderer: ColorizedNameRenderer,
                cellRendererParams: {
                    emptyValue: t('common.unassigned'),
                },
            },
            {
                ...getColumnDef('sum', { group: false, hide: true }),
                headerName: t('common.sum'),
                // Not falsy: a sum of 0 is data, '-' means nothing entered.
                valueGetter: ({ data }) => (data?.sum == null ? null : Math.round(data.sum)),
                valueFormatter: (params) => (params.value == null ? '-' : formatNumber(params.value)),
            },
            {
                ...getColumnDef('released', { group: true, hide: true }),
                headerName: t('field.status'),
                initialSort: 'asc',
                valueGetter: ({ data }) =>
                    data?.released === true
                        ? ReleasedTypeText.RELEASED
                        : data?.released === false
                          ? ReleasedTypeText.NOT_RELEASED
                          : ReleasedTypeText.UNDEFINED,
                cellRenderer: CorrectedTotalsStatusCellRenderer,
            },
            {
                ...getColumnDef('location', { group: true, hide: true }),
                headerName: t('field.partner'),
            },
        ],
        [getColumnDef, t]
    );

    const tabs = useMemo(
        () => [
            {
                label: t('volume.tabOfferedReports'),
                tabPanel: <OfferedReportsTab columnDefs={offeredReportsColumns} date={dayJsDate} />,
            },
            {
                label: t('volume.tabStrategicVolumes'),
                tabPanel: (
                    <StrategicVolumeReportsTab
                        ref={strategicVolumeRef}
                        isTEFUser={isTEFUser}
                        columnDefs={strategicVolumesColumns}
                        date={dayJsDate}
                        onSelectionChanged={handleStrategicVolumeSelection}
                    />
                ),
            },
            {
                label: t('volume.tabTotalsCorrected'),
                tabPanel: <CorrectedTotalsTab columnDefs={totalsColumns} date={dayJsDate} />,
            },
        ],
        [
            offeredReportsColumns,
            dayJsDate,
            isTEFUser,
            strategicVolumesColumns,
            handleStrategicVolumeSelection,
            totalsColumns,
            t,
        ]
    );
    const columnDefs = useMemo(() => {
        switch (openedTab) {
            case TABS.OFFERED_REPORTS:
                return offeredReportsColumns;
            case TABS.STRATEGIC_VOLUMES:
                return strategicVolumesColumns;
            case TABS.CORRECTED_TOTALS:
                return totalsColumns;
            default:
                return [];
        }
    }, [offeredReportsColumns, openedTab, strategicVolumesColumns, totalsColumns]);

    const groupByOptions = useMemo(() => {
        return columnDefs
            .filter(({ context }) => context.canBeGrouped)
            .map((column) => ({ value: column.field, label: column.headerName }));
    }, [columnDefs]);

    const columnOptions = useMemo(() => {
        return columnDefs
            .filter(({ context }) => context.canBeHidden && !context.alwaysHide)
            .map((column) => ({ value: column.field, label: column.headerName }));
    }, [columnDefs]);

    const handleGroupByChange = useCallback(
        (value: string): void => {
            // const columnsToHide = hiddenColumns.filter((column) => column !== groupBy);
            // const columnDef = columnDefs.find((column) => column.field === value);

            // if (!columnsToHide.includes(value) && !columnDef?.context?.alwaysHide) {
            //     columnsToHide.push(value);
            // }
            // setHiddenColumns(columnsToHide);
            setGroupBy(value);
        },
        [setGroupBy]
    );

    return (
        <Container wide={false}>
            <GridContainer sx={undefined}>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                    <FormControl fullWidth={true}>
                        <CustomDatePicker
                            label={t('field.month')}
                            date={dayJsDate}
                            views={['year', 'month']}
                            openTo="month"
                            onChange={(newValue?: Dayjs) =>
                                handleDatePickerMonthChange(dayJsDate, newValue, (newDate: Dayjs) =>
                                    setSelectedDate(newDate.toISOString())
                                )
                            }
                        ></CustomDatePicker>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                    <CustomSelect
                        label={t('volume.groupBy')}
                        id="group-by-select"
                        selectedValue={groupBy}
                        options={groupByOptions}
                        onChange={handleGroupByChange}
                        autoWidth={false}
                    >
                        {groupByOptions.map(({ value, label }) => (
                            <MenuItem value={value} key={value}>
                                {label}
                            </MenuItem>
                        ))}
                    </CustomSelect>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                    <CustomSelect
                        label={t('field.columns')}
                        id="show-columns-select"
                        selectedValue={hiddenColumns}
                        options={columnOptions}
                        multiple={true}
                        renderValue={(selected: string[]) => (
                            // notranslate span: the value swaps in place as columns are toggled —
                            // a translation extension rewriting it leaves stale text stuck (CALL-760)
                            <span className="notranslate">
                                {selected.length === 1
                                    ? t('volume.oneColumnHidden', {
                                          column: columnDefs.find((c) => c.field === selected[0])?.headerName,
                                      })
                                    : selected.length
                                      ? t('volume.columnsHidden', { count: selected.length })
                                      : ''}
                            </span>
                        )}
                        onChange={setHiddenColumns}
                    >
                        {columnOptions.map(({ value, label }) => (
                            <MenuItem value={value} key={value}>
                                <ListItemText primary={label} />
                                <Checkbox
                                    checked={!hiddenColumns.includes(value)}
                                    checkedIcon={<EyeIcon />}
                                    icon={<EyeOffIcon />}
                                />
                            </MenuItem>
                        ))}
                    </CustomSelect>
                </Grid>
                {canMarkRelease && selectedStrategicVolumeRows.length > 0 && (
                    <Grid item xs={12} sm={6} md={3} lg={2} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button
                            variant="contained"
                            color="orange"
                            sx={{
                                minHeight: '2.5rem',
                                lineHeight: '1.5',
                            }}
                            onClick={releaseSelectedStrategicVolumes}
                        >
                            {t('volume.markForRelease')}
                        </Button>
                    </Grid>
                )}
                <Tabs tabs={tabs} defaultTab={openedTab} onChange={handleTabChange} />
            </GridContainer>

            {/* History popover for sum column */}
            <CardPopover anchorEl={hoveredCell} onClose={popoverClose}>
                {hoveredCellHistoryList && (
                    <SumHistoryPopover
                        history={hoveredCellHistoryList.history || []}
                        users={users}
                        offeredCluster={{
                            worktypeName: hoveredCellHistoryList.worktypeName,
                            channelName: hoveredCellHistoryList.channelName,
                            taskName: hoveredCellHistoryList.taskName,
                        }}
                    />
                )}
            </CardPopover>
        </Container>
    );
};

export default VolumeOverview;
